import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/home";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      nav(next, { replace: true });
    } catch (e) {
      setErr(e.message || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = async () => {
    setErr(""); setGoogleBusy(true);
    try {
      await loginWithGoogle();
      // Supabase redirects to Google; onAuthStateChange handles the callback
    } catch (e) {
      setErr(e.message || "Google sign-in failed");
      setGoogleBusy(false);
    }
  };

  const fillDemo = () => { setEmail("maya@nojobnovc.com"); setPassword("GritOverGold!2026"); };

  return (
    <main className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <h1 className="font-display font-extrabold text-5xl uppercase tracking-tight mb-2">
        Welcome <span className="highlight-marker">back</span>
      </h1>
      <p className="font-hand text-2xl text-ink/70 mb-8">— ready to log another dollar?</p>

      {/* Google OAuth */}
      <button
        onClick={onGoogle}
        disabled={googleBusy}
        className="brutal-btn w-full mb-4 flex items-center justify-center gap-3"
        data-testid="login-google-btn"
      >
        <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
          <path d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 2.9l5.7-5.7C34.5 6.7 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 20-9 20-20 0-1.2-.1-2-.4-3.5z" fill="#FFC107"/>
          <path d="M6.3 14.7l6.6 4.8C14.6 16 19 12 24 12c3.1 0 5.8 1.1 7.9 2.9l5.7-5.7C34.5 6.7 29.5 4 24 4c-7.7 0-14.3 4.4-17.7 10.7z" fill="#FF3D00"/>
          <path d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.2 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-8H6.1C9.4 37.5 16.2 44 24 44z" fill="#4CAF50"/>
          <path d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C37.2 38.5 44 33 44 24c0-1.2-.1-2-.4-3.5z" fill="#1976D2"/>
        </svg>
        {googleBusy ? "Redirecting…" : "Continue with Google"}
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-ink/20" />
        <span className="font-mono text-xs text-ink/50 uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-ink/20" />
      </div>

      <form onSubmit={onSubmit} className="brutal-card p-6 space-y-4" data-testid="login-form">
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Email</div>
          <input
            className="brutal-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email-input"
          />
        </label>
        <label className="block">
          <div className="font-mono text-xs uppercase tracking-widest mb-1">Password</div>
          <input
            className="brutal-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password-input"
          />
        </label>
        {err && (
          <div className="border-2 border-marker-red bg-marker-red/10 p-3 font-mono text-sm" data-testid="login-error">
            {err}
          </div>
        )}
        <button type="submit" disabled={busy} className="brutal-btn brutal-btn-yellow w-full" data-testid="login-submit-btn">
          {busy ? "Logging in…" : "Log in"}
        </button>
        <button type="button" onClick={fillDemo} className="brutal-btn w-full text-xs" data-testid="login-demo-btn">
          Use demo player credentials
        </button>
      </form>
      <p className="mt-6 font-mono text-sm">
        New here?{" "}
        <Link to="/register" className="underline decoration-marker-red decoration-2 underline-offset-4 font-bold">
          Join the challenge
        </Link>
      </p>
    </main>
  );
}
